import { useEffect, useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDate, money } from '../shared/format.ts';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, Empty, ErrorBox, Notice, Spinner, StatusBadge, useAction, useLoad } from '../shared/ui.tsx';

/** Office work queues. Filters live on the server (PAPER_TABS) so counts and lists always agree. */
const TABS = [
  ['review', 'Awaiting review'],
  ['ready', 'Ready to assign'],
  ['assigned', 'Assigned'],
  ['recalled', 'Recalled'],
  ['completed', 'Completed'],
  ['return', 'Awaiting return'],
  ['all', 'All'],
] as const;
type Tab = (typeof TABS)[number][0];

function readTab(path: string): Tab {
  const t = new URLSearchParams(path.split('?')[1] ?? '').get('tab');
  return (TABS.find(([k]) => k === t)?.[0] ?? 'review') as Tab;
}

export function PaperList() {
  const { me, can } = useAuth();
  const { path, navigate } = useRouter();
  const tab = readTab(path);
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [panel, setPanel] = useState<'fee' | 'assign' | null>(null);
  const [result, setResult] = useState<{ tone: 'ok' | 'warn'; text: string; failures: { civilosNumber?: string; error?: string }[] } | null>(null);
  const counts = useLoad(() => api.get<{ counts: Record<string, number> }>('/api/papers/tab-counts'), [tab, result]);
  const list = useLoad(
    () => api.get<{ papers: any[] }>(`/api/papers?limit=500&tab=${tab}&q=${encodeURIComponent(submitted)}`),
    [tab, submitted, result],
  );
  useEffect(() => {
    setSel(new Set());
    setPanel(null);
  }, [tab, submitted]);

  const rows = list.data?.papers ?? [];
  const allOn = rows.length > 0 && rows.every((p) => sel.has(p.id));
  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const selected = rows.filter((p) => sel.has(p.id));
  const ids = selected.map((p) => p.id);
  const done = (r: { okCount: number; failed: number; results: any[] }, what: string) => {
    setResult({
      tone: r.failed ? 'warn' : 'ok',
      text: `${what}: ${r.okCount} paper${r.okCount === 1 ? '' : 's'} updated${r.failed ? `, ${r.failed} skipped` : ''}.`,
      failures: r.results.filter((x) => !x.ok),
    });
    setSel(new Set());
    setPanel(null);
  };

  return (
    <div className="stack">
      <div className="row between">
        <h1>Papers</h1>
        <Link to="/papers/new" className="btn primary">
          New intake
        </Link>
      </div>
      {new URLSearchParams(path.split('?')[1] ?? '').get('approved') && (
        <Notice tone="ok">✓ Approved {new URLSearchParams(path.split('?')[1] ?? '').get('approved')}. That was the last paper waiting for intake — all caught up.</Notice>
      )}
      <div className="tabs paper-tabs" role="tablist" aria-label="Paper queues">
        {TABS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => (setResult(null), navigate(`/papers?tab=${k}`))}>
            {label}
            {counts.data && <span className="tab-count">{counts.data.counts[k] ?? 0}</span>}
          </button>
        ))}
      </div>
      <Card>
        <form
          className="row gap wrap"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = q.trim().toUpperCase();
            // A barcode scanner types the full number + Enter: open it directly.
            if (/^\d{6}-\d+$/.test(trimmed) || /^[A-Z0-9]+-\d{2}-[A-Z]+-\d+$/.test(trimmed)) {
              api.get(`/api/papers/by-number/${encodeURIComponent(trimmed)}`).then((d: any) => navigate(`/papers/${d.paper.id}`)).catch(() => setSubmitted(q));
            } else setSubmitted(q);
          }}
        >
          <input className="grow" placeholder="Search number, case, defendant, address — or scan a barcode" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <button className="btn">Search</button>
          {submitted && (
            <button type="button" className="btn ghost" onClick={() => (setQ(''), setSubmitted(''))}>
              Clear
            </button>
          )}
        </form>
      </Card>

      {result && (
        <Notice tone={result.tone}>
          {result.text}
          {result.failures.length > 0 && (
            <ul className="bulk-fails">
              {result.failures.map((f, i) => (
                <li key={i}>
                  <span className="mono">{f.civilosNumber}</span> — {f.error}
                </li>
              ))}
            </ul>
          )}
        </Notice>
      )}

      {selected.length > 0 && (
        <div className="bulk-bar" role="region" aria-label="Bulk actions">
          <strong>{selected.length} selected</strong>
          {can('payments.record') && (
            <button className={`btn sm ${panel === 'fee' ? 'primary' : ''}`} onClick={() => setPanel(panel === 'fee' ? null : 'fee')}>
              Set fee
            </button>
          )}
          {can('custody.scan') && (
            <button className={`btn sm ${panel === 'assign' ? 'primary' : ''}`} onClick={() => setPanel(panel === 'assign' ? null : 'assign')}>
              Assign
            </button>
          )}
          <Link className="btn sm" to={`/papers/print?kind=labels&ids=${ids.join(',')}`}>
            Print labels
          </Link>
          <Link className="btn sm" to={`/papers/print?kind=covers&ids=${ids.join(',')}`}>
            Print cover sheets
          </Link>
          <button className="btn sm ghost" onClick={() => setSel(new Set())}>
            Clear selection
          </button>
        </div>
      )}
      {panel === 'fee' && <BulkFee ids={ids} onDone={(r) => done(r, 'Fee set')} />}
      {panel === 'assign' && <BulkAssign ids={ids} onDone={(r) => done(r, 'Assigned')} />}

      <Card>
        {list.loading && !list.data && <Spinner />}
        <ErrorBox error={list.error} />
        {list.data && rows.length === 0 && <Empty>No papers here.</Empty>}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="check-col">
                    <input type="checkbox" aria-label="Select all" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(rows.map((p) => p.id)))} />
                  </th>
                  <th>Paper #</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Defendant</th>
                  <th>Address</th>
                  <th>Case</th>
                  <th>Court date</th>
                  {tab === 'ready' && <th>Set for</th>}
                  <th>Fee</th>
                  <th>Server</th>
                  <th>Attempts</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className={`clickable ${sel.has(p.id) ? 'selected' : ''}`} onClick={() => navigate(`/papers/${p.id}`)}>
                    <td className="check-col" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${p.civilos_number}`} checked={sel.has(p.id)} onChange={() => toggle(p.id)} />
                    </td>
                    <td className="mono">{p.civilos_number}</td>
                    <td>
                      <StatusBadge status={p.status} labels={me!.agency.statusLabels} /> {p.on_hold && <Badge tone="bad">hold</Badge>} {p.recalled && <Badge tone="bad">recalled</Badge>}{' '}
                      {p.manual_urgent && <Badge tone="warn">urgent</Badge>} {p.iv_d && <Badge tone="info">IV-D</Badge>}
                    </td>
                    <td>{p.paper_type_code.replace(/_/g, ' ').toLowerCase().replace(/^./, (c: string) => c.toUpperCase())}</td>
                    <td>{p.defendant}</td>
                    <td className="muted">{p.address}</td>
                    <td className="mono">{p.case_number}</td>
                    <td>{fmtDate(p.court_date)}</td>
                    {tab === 'ready' && <td>{p.next_service_label ?? '—'}</td>}
                    <td>{money(p.fee_cents)}</td>
                    <td>{p.server_name ?? '—'}</td>
                    <td>{p.attempt_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows.length >= 500 && <p className="small muted">Showing the first 500. Search to narrow the list.</p>}
      </Card>
    </div>
  );
}

function BulkFee({ ids, onDone }: { ids: string[]; onDone: (r: any) => void }) {
  const [amount, setAmount] = useState('0');
  const [reason, setReason] = useState('');
  const a = useAction();
  return (
    <Card title={`Set fee for ${ids.length} paper${ids.length === 1 ? '' : 's'}`}>
      <form
        className="row gap wrap bulk-form" data-unsaved="true"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => onDone(await api.post('/api/papers/bulk', { action: 'fee', paperIds: ids, feeCents: Math.round(Number(amount) * 100), reason: reason.trim() || undefined })));
        }}
      >
        <label className="field">
          <span className="field-l">Fee ($)</span>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required style={{ width: 120 }} />
        </label>
        <label className="field grow">
          <span className="field-l">Reason (optional)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Magistrate papers — no fee" />
        </label>
        <button className="btn primary" disabled={a.busy || amount === ''}>
          {a.busy ? 'Saving…' : 'Apply fee'}
        </button>
      </form>
      <p className="small muted">$0 marks them fee-exempt. Papers already paid, or already past review, are skipped and listed.</p>
      <ErrorBox error={a.error} />
    </Card>
  );
}

function BulkAssign({ ids, onDone }: { ids: string[]; onDone: (r: any) => void }) {
  const meta = useLoad(() => api.get('/api/meta'));
  const [server, setServer] = useState('');
  const a = useAction();
  return (
    <Card title={`Assign ${ids.length} paper${ids.length === 1 ? '' : 's'}`}>
      <form
        className="row gap wrap bulk-form" data-unsaved="true"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => onDone(await api.post('/api/papers/bulk', { action: 'assign', paperIds: ids, serverId: server, mode: 'retained' })));
        }}
      >
        <label className="field">
          <span className="field-l">Server</span>
          <select value={server} onChange={(e) => setServer(e.target.value)} required aria-label="Assign to server">
            <option value="">Choose…</option>
            {(meta.data?.servers ?? []).map((s: any) => (
              <option key={s.id} value={s.id}>
                {s.display_name}
                {s.badge_number ? ` (${s.badge_number})` : ''}
              </option>
            ))}
          </select>
        </label>
        <button className="btn primary" disabled={a.busy || !server}>
          {a.busy ? 'Assigning…' : 'Assign'}
        </button>
      </form>
      <p className="small muted">They show up on the deputy’s “Papers to pick up.” Custody changes when they’re scanned out to the deputy. Papers that aren’t ready (payment, hold, recalled) are skipped and listed.</p>
      <ErrorBox error={a.error} />
    </Card>
  );
}
